import type { DocStatus, GstTreatment, Material, Site, UserRole, Vendor } from './types'

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------
export const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'System Administrator',
  cxo: 'CXO (View Only)',
  cmd: 'Chairman & Managing Director',
  project_head: 'Project Head',
  project_manager: 'Project Manager',
  accounts_spoc: 'Accounts (SPOC)',
  procurement_head: 'Procurement Manager',
  procurement_manager: 'Procurement Executive',
  stores_head: 'Head of Stores',
  store_executive: 'Stores Executive (HO)',
  site_construction_manager: 'Resident Construction Manager',
  storeman: 'Site Store-man',
  site_engineer: 'Site Engineer',
  cfo: 'Chief Financial Officer',
}

/**
 * Roles that see every site.
 *
 * The Project Manager is deliberately absent. He holds half the sites, not
 * all of them, and the other half are none of his business — the database
 * grants him his own through can_access_site, not through this list.
 */
export const HO_ROLES: UserRole[] = [
  'admin', 'cxo', 'procurement_head', 'procurement_manager', 'stores_head', 'store_executive',
  'cmd', 'project_head', 'accounts_spoc',
]

export const isHoRole = (r?: UserRole | null) => !!r && HO_ROLES.includes(r)
export const isReadOnly = (r?: UserRole | null) => r === 'cxo'

/**
 * Whose document lists must NOT be pinned to one site.
 *
 * Before the Board resolution every role was one of two things: head office,
 * which sees all 31 sites, or site staff, who see the single site named on
 * their profile. Every list screen encoded that as
 * `useState(isHoRole(role) ? '' : profile.site_id)`.
 *
 * A Project Manager is neither. He holds roughly half the sites and his
 * profile says HO, so that line quietly filtered his lists down to head-office
 * documents and hid every requisition he was supposed to approve.
 *
 * Leaving his filter empty is correct AND safe: the database already narrows
 * what he can read to his own sites, through can_access_site. The screen does
 * not need to help.
 */
export const seesManySites = (r?: UserRole | null) => isHoRole(r) || r === 'project_manager'

/**
 * The sites a person may usefully pick in a filter. A Project Manager offered
 * a colleague's site would select it and see nothing, which looks like a
 * fault rather than a boundary.
 */
export function visibleSites<T extends { id: string; project_manager_id?: string | null }>(
  sites: T[], role?: UserRole | null, myId?: string | null,
): T[] {
  if (role !== 'project_manager') return sites
  return sites.filter((s) => s.project_manager_id && s.project_manager_id === myId)
}

/**
 * Capability matrix. Mirrors the RLS policies in the database — the UI hides
 * what the server would refuse anyway, so a user never meets a dead end.
 */
export const CAN: Record<string, UserRole[]> = {
  raiseIndent: ['site_construction_manager', 'store_executive', 'admin'],
  createPr: ['storeman', 'store_executive', 'stores_head', 'admin'],
  approvePr: ['stores_head', 'admin'],
  createIstPr: ['stores_head', 'admin'],
  // 0211, 14-10-2026: cancel a transfer requisition no transfer exists against,
  // when the material is no longer required. rpc_ist_pr_cancel enforces it.
  cancelIstPr: ['stores_head', 'admin'],
  manageIst: ['stores_head', 'admin'],
  // Board 01-09-2026: the Head of Procurement raises orders herself as well.
  // Two guards in the database keep four eyes on anything she raises — her
  // stand-in right at stage 1 is withdrawn on her own orders, and stage 4 (her
  // own departmental sign-off) is cleared on raising and recorded as such. See
  // migration 20260927000001. It still passes a Procurement Manager, Stores,
  // Accounts and the CMD.
  createPo: ['procurement_manager', 'procurement_head', 'admin'],
  reviewPo: ['stores_head', 'admin'],
  approvePo: ['procurement_head', 'admin'],
  despatchPo: ['procurement_manager', 'procurement_head', 'admin'],
  // Board 11-08-2026: closing an order and declaring one null and void moved
  // to the Head of Procurement and both Procurement Managers.
  closePo: ['procurement_head', 'procurement_manager', 'admin'],
  createGrn: ['storeman', 'store_executive', 'admin'],
  approveGrn: ['stores_head', 'admin'],
  createMin: ['storeman', 'store_executive', 'admin'],
  createMrn: ['storeman', 'store_executive', 'admin'],
  approveStoreDocs: ['stores_head', 'admin'],
  adjustInventory: ['stores_head', 'admin'],
  manageAssets: ['stores_head', 'admin'],
  runWeekly: ['stores_head', 'procurement_head', 'admin'],
  managePayables: ['procurement_manager', 'procurement_head', 'admin'],
  // "All CRUD operations related to payments only." Accounts moves money and
  // does nothing else. Writing off an unadjusted advance is NOT a payment —
  // it stays with the Head of Procurement.
  recordPayment: ['procurement_manager', 'procurement_head', 'accounts_spoc', 'admin'],
  settleAdvance: ['procurement_head', 'admin'],
  // Booking a vendor's invoice, matching it against the receipt, and booking
  // the credit note when he over-billed, are the classic accounts-payable
  // duties — MIRO in SAP, and a finance transaction there rather than a
  // purchasing one. Accounts joins Procurement on all three so the work does
  // not stop when a Procurement Manager is away.
  recordInvoice: ['procurement_manager', 'procurement_head', 'accounts_spoc', 'admin'],
  createCreditNote: ['procurement_manager', 'procurement_head', 'accounts_spoc', 'admin'],
  // Deleting one is the opposite act: it puts money BACK on what a vendor is
  // owed. Whoever can do that and also pay him has both halves of a fraud in
  // one pair of hands, so it stays with Procurement.
  deleteCreditNote: ['procurement_manager', 'procurement_head', 'admin'],
  // Master data sits with whoever owns the consequences of getting it wrong.
  // A wrong conversion factor is a stores problem; a wrong GSTIN is a
  // procurement problem. The administrator can always step in.
  manageMaterials: ['stores_head', 'admin'],
  // The Head of Stores counts in these units and Procurement buys in them, so
  // both own the list. Nobody else may add to it — a master anyone can extend
  // is not a master, it is a free-text field with extra steps.
  manageUoms: ['stores_head', 'procurement_head', 'procurement_manager', 'admin'],
  manageVendors: ['procurement_manager', 'procurement_head', 'admin'],
  // READ the vendor master. Wider than maintaining it: Accounts pays against
  // these vendors and the board reads the liability figures behind them, so
  // both need to open a vendor record without telephoning Procurement for a
  // GSTIN. Kept separate from manageVendors so widening the view can never
  // widen the edit.
  viewVendors: ['procurement_manager', 'procurement_head', 'admin',
                'accounts_spoc', 'cmd', 'cxo'],
  manageSites: ['procurement_head', 'procurement_manager', 'admin'],
  manageTerms: ['procurement_head', 'procurement_manager', 'admin'],
  // The rate history is a buying tool; the Head of Stores reads it to sanity
  // check what the sites are being charged.
  // Accounts judges whether a price is reasonable before counter-signing an
  // order; the CMD and the Project Head answer for the spend. All read-only —
  // the rate history is written by the system when an order is approved, never
  // by hand.
  viewRates: ['procurement_head', 'procurement_manager', 'stores_head',
              'accounts_spoc', 'cmd', 'project_head', 'admin'],
  // The vendor invoice register and the GST input-tax-credit position.
  // Deliberately NOT the Procurement Managers: they enter invoices, but the tax
  // position belongs to Accounts and the people who answer for it. A GST return
  // is not a purchasing document.
  viewItc: ['accounts_spoc', 'stores_head', 'procurement_head', 'project_head',
            'cmd', 'cxo', 'admin'],
  // The goods-receipt register. Accounts needs it because sites buy locally in
  // an emergency and those bills exist nowhere else — they are attachments on a
  // goods receipt. Stores and Procurement see it because it is their document;
  // the Project Head and the CMD because they answer for the spend.
  // A Project Manager joined this list on 11-09-2026, for the sites assigned
  // to him. He is asked weekly whether a delivery arrived and whether the bill
  // is with Accounts; before this he had to telephone somebody to find out.
  viewGrnRegister: ['accounts_spoc', 'stores_head', 'procurement_head',
                    'procurement_manager', 'project_head', 'cmd', 'cxo', 'admin',
                    'project_manager'],
  // Only Accounts ticks a bill off as keyed into Tally, or queries the
  // paperwork. Everyone else on the register above is reading.
  markGrnExtracted: ['accounts_spoc', 'admin'],
  // The liabilities dashboard. Asked for by the board: the CMD, the CXOs, the
  // Project Head, Accounts, the Head of Stores and the Head of Procurement.
  // Read-only for every one of them — nothing on that screen writes anything.
  //
  // A Project Manager was deliberately left off the first version, and the
  // reason given was that "a manager seeing the whole company's vendor exposure
  // is a different decision from seeing his own sites". That reason was right,
  // and on 11-09-2026 the difference was actually built rather than argued
  // about: migration 20261024000001 scopes vendor_payables to the sites he is
  // named on and makes v_payable_settlement honour row level security, so every
  // figure on the screen — including the one labelled as a company total — is
  // computed from his rows alone.
  //
  // So he is on the list now, and what he sees is not what the CMD sees.
  viewLiabilities: ['cmd', 'cxo', 'project_head', 'accounts_spoc',
                    'stores_head', 'procurement_head', 'admin', 'project_manager'],
  // The document trail on a purchase order. Read-only for all of them: the
  // trail links out to each document's own screen, where that document's
  // permissions and the approval chain still apply.
  // A store-man is deliberately off the list — he works one goods receipt at a
  // time and has no business reading a vendor's payment history.
  // The database enforces this too, in may_view_document_trail().
  viewDocumentTrail: ['project_manager', 'procurement_manager', 'procurement_head',
                      'stores_head', 'project_head', 'cmd', 'cxo',
                      'accounts_spoc', 'admin'],
  // Naming the local shop behind an OTHERS placeholder, once somebody has
  // found out who it was. Procurement's job — they own the vendor master.
  identifyAdhocVendor: ['procurement_manager', 'procurement_head', 'admin'],
  // Linking an approved ad-hoc receipt to the purchase order it was really
  // delivered against (29-09-2026, migration 0182). The same people as above;
  // rpc_link_adhoc_grn_to_po enforces it in the database.
  linkAdhocToPo: ['procurement_manager', 'procurement_head', 'admin'],
  // Requisition housekeeping (30-09-2026, migration 0188): cancel a requisition
  // nothing was ordered against, or close what is no longer required.
  // rpc_pr_cancel / rpc_pr_close_lines enforce the same list.
  prHousekeeping: ['procurement_manager', 'procurement_head', 'admin'],
  // The petty cash register. Read-only — the purchase is recorded on the goods
  // receipt at site; this only reports it.
  //
  // The Head of Stores is on the list because he APPROVES these receipts. He is
  // the one control point between a store-man spending site cash and that cash
  // leaving the float, so he of all people should be able to see the running
  // total. He was left off the first version and added on review.
  // And the Project Manager, from 11-09-2026, for his own sites only. Cash
  // spent at a site he answers for is the clearest case on this list: it is
  // his budget, and he is the person who will be asked why it went.
  viewPettyCash: ['accounts_spoc', 'procurement_head', 'procurement_manager',
                  'project_head', 'stores_head', 'admin', 'project_manager'],
  exportInventory: ['stores_head', 'admin'],
  // "Where is this material?" on Master Data (11-09-2026): site-wise stock,
  // value, orders due, requisitions and stock health for one material.
  // Read-only; decided by the System Administrator.
  viewMaterialDistribution: ['stores_head', 'procurement_head', 'project_head', 'cmd', 'cxo', 'admin'],
  // "Orders awaiting delivery" on the Vendors tab (12-09-2026): the purchase
  // orders a vendor has acknowledged and not yet delivered. Read-only, and
  // extended to the Procurement Managers who do the chasing.
  viewVendorOrders: ['stores_head', 'procurement_head', 'procurement_manager',
                     'project_head', 'cmd', 'cxo', 'admin'],
  manageUsers: ['admin'],
  // Vendor opening liabilities, Board mandate 23-09-2026 (migration 0179).
  // Maker loads the Accounts ledger balances and enters payments from the
  // screenshot; checker approves, returns, rejects, reverses. The database
  // also refuses a checker who is the maker, whatever the role.
  viewOpeningLiabilities: ['accounts_spoc', 'procurement_manager', 'procurement_head',
                           'project_head', 'stores_head', 'cmd', 'cxo', 'admin'],
  makeOpeningLiability: ['accounts_spoc', 'procurement_manager', 'admin'],
  checkOpeningLiability: ['project_head', 'procurement_head', 'admin'],
}

export const can = (role: UserRole | undefined | null, capability: keyof typeof CAN | string) =>
  !!role && (CAN[capability] ?? []).includes(role)

// ---------------------------------------------------------------------------
// The Board's approval chain
// ---------------------------------------------------------------------------
/**
 * Mirrors public.approval_chain_steps, for the same reason CAN mirrors the RLS
 * policies: the screen should never offer a button the server is going to
 * refuse.
 *
 * The document's STATUS does not move through these stages. It stays
 * SUBMITTED until the last signature and then becomes APPROVED — which is why
 * every downstream feature carried on working unchanged.
 */
export interface ChainStep {
  step: number
  role: UserRole
  label: string
  hint: string
}

export const APPROVAL_CHAIN: Record<'PR' | 'PO', ChainStep[]> = {
  PR: [
    { step: 1, role: 'stores_head', label: 'Head of Stores',
      hint: 'Checks whether the material is already sitting at another site before anyone approves buying it.' },
    { step: 2, role: 'project_manager', label: 'Project Manager',
      hint: 'The manager assigned to this site. Nobody else may act.' },
    { step: 3, role: 'project_head', label: 'Project Head',
      hint: 'Company-wide view. Once cleared, the requisition can become a purchase order.' },
  ],
  PO: [
    { step: 1, role: 'procurement_manager', label: 'The other Procurement Executive',
      hint: 'Never the manager who raised it. Four eyes on the vendor, the rates and the terms.' },
    { step: 2, role: 'stores_head', label: 'Head of Stores',
      hint: 'Confirms quantities, units and specifications against what the sites need.' },
    { step: 3, role: 'accounts_spoc', label: 'Accounts',
      hint: 'Confirms the commercial terms, taxes and payment schedule are workable.' },
    { step: 4, role: 'procurement_head', label: 'Procurement Manager',
      hint: 'Departmental sign-off.' },
    { step: 5, role: 'cmd', label: 'CMD',
      hint: 'Final signature. The order may not go to the vendor before this.' },
  ],
}

export const chainLength = (docType: 'PR' | 'PO') => APPROVAL_CHAIN[docType].length
export const chainStep = (docType: 'PR' | 'PO', step: number) =>
  APPROVAL_CHAIN[docType].find((s) => s.step === step) ?? null

export interface StepContext {
  /** The signed-in user's profile id. */
  myId?: string | null
  /** Project Manager assigned to the document's site. PR step 2 only. */
  sitePmId?: string | null
  /** Who raised the document. PO step 1 only. */
  creatorId?: string | null
}

/**
 * May this person clear this stage? Mirrors public.fn_may_clear_step.
 *
 * Two stages carry a rule beyond "your role matches":
 *   PR 2 — must be the Project Manager assigned to that site.
 *   PO 1 — must NOT be the manager who raised the order. If the other manager
 *          is unavailable the Head of Procurement may stand in, and the
 *          record says so.
 */
export function mayClearStep(
  docType: 'PR' | 'PO',
  step: number,
  role: UserRole | null | undefined,
  ctx: StepContext = {},
): boolean {
  if (!role) return false
  if (role === 'admin') return true

  const s = chainStep(docType, step)
  if (!s) return false

  if (docType === 'PR' && s.role === 'project_manager') {
    return role === 'project_manager' && !!ctx.myId && ctx.myId === ctx.sitePmId
  }

  if (docType === 'PO' && step === 1) {
    if (role === 'procurement_manager') return !!ctx.myId && ctx.myId !== ctx.creatorId
    // Board-sanctioned stand-in — but NOT on an order she raised herself.
    // Board 01-09-2026 let the Head of Procurement raise orders; without this
    // second condition she could clear the four-eyes step on her own work, at
    // the one stage that exists to prevent exactly that. Mirrors
    // fn_may_clear_step in the database, which is what actually enforces it.
    if (role === 'procurement_head') return !!ctx.myId && ctx.myId !== ctx.creatorId
    return false
  }

  return role === s.role
}

/** True when the Head of Procurement is covering for the second Procurement Manager. */
export const isStandIn = (docType: 'PR' | 'PO', step: number, role?: UserRole | null) =>
  docType === 'PO' && step === 1 && role === 'procurement_head'

// ---------------------------------------------------------------------------
// Status presentation
// ---------------------------------------------------------------------------
export const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Awaiting approval',
  RETURNED: 'Returned for revision',
  REJECTED: 'Rejected',
  APPROVED: 'Approved',
  REVIEWED: 'Reviewed — awaiting final approval',
  VENDIN: 'Sent to vendor',
  VENDACK: 'Vendor acknowledged',
  PARTIALLY_FULFILLED: 'Partially fulfilled',
  FULFILLED: 'Fulfilled',
  PARTIALLY_RECEIVED: 'Partially received',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
  TRANSFER_INITIATED: 'Transfer initiated',
  IN_TRANSIT: 'In transit',
  DELIVERED: 'Delivered',
  ACCEPTED: 'Accepted',
  CONVERTED_TO_PR: 'Converted to PR',
  FULFILLED_FROM_STOCK: 'Fulfilled from site stock',
  FORCED_CLOSE: 'Null and void',
  // 0227/0228: a payable approved on or before 31-10-2026, already inside the
  // Accounts opening balance in Project Finance. Paid from Finance, not here.
  IN_OPENING: 'Taken into opening',
}

export const STATUS_TONE: Record<string, 'grey' | 'amber' | 'green' | 'red' | 'blue' | 'violet'> = {
  DRAFT: 'grey',
  SUBMITTED: 'amber',
  RETURNED: 'amber',
  REJECTED: 'red',
  APPROVED: 'green',
  REVIEWED: 'blue',
  VENDIN: 'blue',
  VENDACK: 'violet',
  PARTIALLY_FULFILLED: 'blue',
  FULFILLED: 'green',
  PARTIALLY_RECEIVED: 'blue',
  CLOSED: 'grey',
  CANCELLED: 'grey',
  FORCED_CLOSE: 'red',
  TRANSFER_INITIATED: 'amber',
  IN_TRANSIT: 'blue',
  DELIVERED: 'green',
  ACCEPTED: 'blue',
  CONVERTED_TO_PR: 'green',
  FULFILLED_FROM_STOCK: 'green',
  IN_OPENING: 'grey',
}

/** REJECTED is terminal everywhere; the originator must start a fresh document. */
export const isTerminal = (s: DocStatus | string) =>
  ['REJECTED', 'CANCELLED', 'CLOSED', 'FULFILLED'].includes(s)

/** RETURNED sends the document back to whoever created it, still editable. */
export const isEditable = (s: DocStatus | string) => ['DRAFT', 'RETURNED'].includes(s)

// ---------------------------------------------------------------------------
// GST
// ---------------------------------------------------------------------------
/**
 * Unregistered vendor  -> no GST at all (matches the approved PO template).
 * Vendor state == billing state -> CGST + SGST, half the rate each.
 * Otherwise -> IGST at the full rate.
 */
export function gstTreatmentFor(vendor?: Vendor | null, billingSite?: Site | null): GstTreatment {
  if (!vendor || !vendor.is_gst_registered) return 'NONE'
  const v = (vendor.state_name ?? '').trim().toUpperCase()
  const s = (billingSite?.state_name ?? '').trim().toUpperCase()
  if (!v || !s) return 'IGST'
  return v === s ? 'CGST_SGST' : 'IGST'
}

export interface GstSplit {
  subtotal: number
  cgst: number
  sgst: number
  igst: number
  gst: number
  total: number
}

export function splitGst(lines: Array<{ line_amount: number; gst_rate_percent: number }>, treatment: GstTreatment): GstSplit {
  const subtotal = round2(lines.reduce((a, l) => a + (l.line_amount || 0), 0))
  if (treatment === 'NONE') {
    return { subtotal, cgst: 0, sgst: 0, igst: 0, gst: 0, total: subtotal }
  }
  const gst = round2(lines.reduce((a, l) => a + round2((l.line_amount || 0) * (l.gst_rate_percent || 0) / 100), 0))
  if (treatment === 'IGST') {
    return { subtotal, cgst: 0, sgst: 0, igst: gst, gst, total: round2(subtotal + gst) }
  }
  const cgst = round2(gst / 2)
  return { subtotal, cgst, sgst: round2(gst - cgst), igst: 0, gst, total: round2(subtotal + gst) }
}

// ---------------------------------------------------------------------------
// Unit-of-measure conversion
//
// Some materials — TMT bar above all — are stocked and counted in NOS but
// bought and delivered in KG. conversion_factor is kilograms per bar, so:
//
//     purchase quantity (KG)  =  stock quantity (NOS) x conversion_factor
//     stock quantity  (NOS)   =  purchase quantity (KG) / conversion_factor
//
// The PO prints the purchase UOM; the storeman counts in the stock UOM. Both
// the PO builder and the GRN screen convert live in both directions.
// ---------------------------------------------------------------------------
export const needsConversion = (m?: Material | null) =>
  !!m && m.unit_of_measure !== m.purchase_uom && Number(m.conversion_factor) !== 1

export const stockToPurchase = (qtyStock: number, factor: number) =>
  round3(qtyStock * (factor || 1))

export const purchaseToStock = (qtyPurchase: number, factor: number) =>
  round3(qtyPurchase / (factor || 1))

/**
 * Counted piece by piece, sold by volume - AAC blocks (NO / CUM, decided
 * 11-09-2026). 10 CUM of 6" block is 555.556 blocks on paper, but half a block
 * cannot be stacked, counted or issued, so a RECEIPT converted from the
 * challan's volume lands on whole blocks. The store-man still overwrites it
 * with what he actually counted.
 *
 * Deliberately narrow: TMT bars and structural steel (NO / KG) keep their
 * three decimals exactly as before - a weight never divides into whole bars.
 */
export const countsWholePieces = (m?: Pick<Material, 'unit_of_measure' | 'purchase_uom' | 'conversion_factor'> | null) =>
  !!m && m.unit_of_measure === 'NO' && m.purchase_uom === 'CUM' && Number(m.conversion_factor) !== 1

/** Challan quantity (purchase UOM) -> stock UOM, whole pieces where the material is counted that way. */
export const receiptToStock = (qtyPurchase: number, m?: Material | null) =>
  countsWholePieces(m)
    ? Math.round(qtyPurchase / (Number(m!.conversion_factor) || 1))
    : purchaseToStock(qtyPurchase, m?.conversion_factor ?? 1)

/**
 * A requisition's estimated rate is per STOCK unit (its amount is quantity in
 * stock UOM x rate). A purchase order's rate is per PURCHASE unit. Carry one
 * into the other: Rs 72 per block / 0.018 CUM per block = Rs 4,000 per CUM;
 * Rs 920 per TMT bar / 10.67 kg per bar = Rs 86.22 per kg.
 */
export const stockRateToPurchaseRate = (ratePerStock: number, factor: number) =>
  round2(ratePerStock / (Number(factor) || 1))

/**
 * A conversion factor, written the way a person says it.
 *
 * The factor is stored as PURCHASE units per STOCK unit, which reads well for
 * a TMT bar (10.67 KG per bar) and terribly for an AAC block, where it is
 * 0.01801802 CUM per block. So under 1 it is flipped: 55.5 NO per CUM - the
 * number on the supplier's quotation, and the one the store-man counts with.
 */
export const factorShort = (n: number) => {
  const v = Math.round((Number(n) || 0) * 1e6) / 1e6
  return String(v)
}

export function conversionText(m?: Pick<Material, 'unit_of_measure' | 'purchase_uom' | 'conversion_factor'> | null): string {
  if (!m) return ''
  const f = Number(m.conversion_factor) || 1
  // Four decimals on the spoken number: the stored factor is 1/55.5 rounded to
  // eight places, so the reverse comes back as 55.499994 and has to be read as
  // the 55.5 on the quotation.
  const said = (n: number) => String(Math.round(n * 1e4) / 1e4)
  return f >= 1
    ? `${said(f)} ${m.purchase_uom} per ${m.unit_of_measure}`
    : `${said(1 / f)} ${m.unit_of_measure} per ${m.purchase_uom}`
}

// ---------------------------------------------------------------------------
// GRN acceptance tolerance
// ---------------------------------------------------------------------------
export const GRN_TOLERANCE_PCT = 10

export interface ToleranceCheck {
  withinTolerance: boolean
  variancePct: number
  ceiling: number
  message: string | null
}

/**
 * Accepted quantity may exceed the PO's outstanding quantity by up to 10%.
 *
 * `pct` since 29-09-2026 (migration 0184): the percentage is a System Setting,
 * company_profile.grn_tolerance_pct, read by useCompanySettings(). Leaving it
 * out gives exactly the old behaviour - GRN_TOLERANCE_PCT, 10.
 */
export function checkTolerance(accepted: number, poRemaining: number,
                               pct: number = GRN_TOLERANCE_PCT): ToleranceCheck {
  if (!poRemaining || poRemaining <= 0) {
    return { withinTolerance: true, variancePct: 0, ceiling: 0, message: null }
  }
  const ceiling = round3(poRemaining * (1 + pct / 100))
  const variancePct = round2(((accepted - poRemaining) / poRemaining) * 100)
  if (accepted <= ceiling + 0.001) {
    return { withinTolerance: true, variancePct, ceiling, message: null }
  }
  return {
    withinTolerance: false,
    variancePct,
    ceiling,
    message: `Accepted ${accepted} exceeds the ${pct}% tolerance ceiling of ${ceiling} ` +
             `(PO outstanding ${poRemaining}). The Head of Stores will see this flagged.`,
  }
}

// ---------------------------------------------------------------------------
// Business week: Saturday 00:00:01 IST -> Friday 23:59:59 IST
// ---------------------------------------------------------------------------
export function weekStart(at: Date = new Date()): Date {
  const d = new Date(at)
  d.setHours(0, 0, 0, 0)
  // getDay(): Sun=0 ... Sat=6.  Saturday must map to itself.
  d.setDate(d.getDate() - ((d.getDay() + 1) % 7))
  return d
}

export function weekEnd(at: Date = new Date()): Date {
  const d = weekStart(at)
  d.setDate(d.getDate() + 6)
  return d
}

export function weekLabel(at: Date = new Date()): string {
  const s = weekStart(at), e = weekEnd(at)
  const f = (x: Date) => `${String(x.getDate()).padStart(2, '0')}-${String(x.getMonth() + 1).padStart(2, '0')}-${x.getFullYear()}`
  return `${f(s)} (Sat) to ${f(e)} (Fri)`
}

export const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

// ---------------------------------------------------------------------------
// Rounding
// ---------------------------------------------------------------------------
export const round2 = (n: number) => Math.round((Number(n) + Number.EPSILON) * 100) / 100
export const round3 = (n: number) => Math.round((Number(n) + Number.EPSILON) * 1000) / 1000
